import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import client from "@/lib/api";
import Divider from "@/components/Divider";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const brandImg = (slug) => `${API_BASE}/api/images/brand/${slug}`;

const FALLBACK_IMG = "https://images.unsplash.com/photo-1592457711340-2412dc07b733?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export function RecipesPreview() {
  const [recipes, setRecipes] = useState([]);
  useEffect(() => {
    client.get("/content/recipes?preview=true").then(({ data }) => setRecipes(data.recipes)).catch(() => {});
  }, []);
  return (
    <div className="bg-mizizi-cream">
      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-16 text-center">
        <div className="overline text-mizizi-terracotta">The Recipes</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-6 leading-none">30 plates. One table.</h1>
        <p className="mt-8 text-lg text-mizizi-charcoal/70">
          Every recipe in the book, at a glance. Eight signature plates open freely — the remaining twenty-two unlock with the cookbook.
        </p>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-10 pb-24">
        {recipes.map((r) => (
          <Link key={r.slug} to={`/preview/${r.slug}`} data-testid={`preview-${r.slug}`} className="recipe-card group block">
            <div className="overflow-hidden aspect-[4/5] bg-mizizi-forest relative">
              <img
                src={brandImg(r.slug)}
                alt={r.title}
                loading="lazy"
                onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                className={`recipe-cover w-full h-full object-cover ${r.locked ? "blur-locked" : ""}`}
              />
              <div className="absolute top-4 left-4 overline bg-mizizi-charcoal/70 text-mizizi-cream px-3 py-1">{r.chapter}</div>
              {r.locked && (
                <div className="absolute top-4 right-4 bg-mizizi-charcoal/70 p-2" aria-label="Locked">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B8873B" strokeWidth="1.6">
                    <rect x="4" y="11" width="16" height="10" rx="1"/>
                    <path d="M8 11V7a4 4 0 018 0v4"/>
                  </svg>
                </div>
              )}
            </div>
            <div className="mt-5">
              <h3 className="recipe-title font-serif text-2xl text-mizizi-charcoal transition-colors">{r.title}</h3>
              <p className="mt-2 text-mizizi-charcoal/60 text-sm">{r.prep_min + r.cook_min} min · {r.difficulty} · Serves {r.servings}</p>
            </div>
          </Link>
        ))}
      </section>

      {/* Unlock CTA */}
      <section className="bg-mizizi-charcoal text-mizizi-cream py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="overline text-mizizi-gold">Twenty-two more waiting</div>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">Take the seat.</h2>
          <p className="mt-6 text-mizizi-cream/70 leading-relaxed">
            The full method, ingredient list, substitutions, and pairing notes for every recipe — plus three menus, foundational sauces, and the pantry guide — live inside the reader.
          </p>
          <Link to="/shop" data-testid="unlock-cta" className="pill pill-primary mt-10 inline-flex">Unlock All 30 Recipes</Link>
        </div>
      </section>
    </div>
  );
}

export function RecipePreviewDetail() {
  const { slug } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    client.get(`/content/recipes/${slug}`).then(({ data }) => {
      setRecipe(data.recipe);
      setLocked(data.locked);
    }).catch(() => {});
  }, [slug]);
  if (!recipe) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl">Loading…</div>;
  return (
    <div className="bg-mizizi-cream">
      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-40 pb-12 text-center">
        <Link to="/preview" data-testid="back-preview" className="overline text-mizizi-terracotta hover:text-mizizi-gold">← All recipes</Link>
        <div className="overline text-mizizi-gold mt-8">{recipe.chapter}</div>
        <h1 className="font-serif font-light text-4xl md:text-6xl mt-4 leading-tight text-mizizi-charcoal">{recipe.title}</h1>
        <p className="mt-6 max-w-2xl mx-auto text-lg italic text-mizizi-charcoal/70">{recipe.story}</p>
      </header>
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <img
          src={brandImg(recipe.slug)}
          alt={recipe.title}
          onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
          className="w-full aspect-[4/3] object-cover"
        />
      </div>
      <Divider />
      {locked ? (
        <div className="max-w-2xl mx-auto text-center px-6 pb-24">
          <div className="overline text-mizizi-terracotta">Locked</div>
          <h2 className="font-serif text-4xl mt-4 text-mizizi-charcoal">This recipe is in the full book.</h2>
          <p className="mt-6 text-mizizi-charcoal/70">Unlock all 30 plates, plus menus, pantry and foundations.</p>
          <Link to="/shop" data-testid="unlock-cta" className="pill pill-primary mt-8 inline-flex">Get the Cookbook</Link>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto px-6 md:px-10 pb-16 text-mizizi-charcoal/85 leading-relaxed">
          <p className="font-serif italic text-xl">A preview of what awaits in the reader.</p>
          <ul className="mt-6 grid grid-cols-3 gap-4 text-center hairline-b pb-8">
            <li><div className="overline text-mizizi-gold">Prep</div><div className="font-serif text-2xl">{recipe.prep_min}′</div></li>
            <li><div className="overline text-mizizi-gold">Cook</div><div className="font-serif text-2xl">{recipe.cook_min}′</div></li>
            <li><div className="overline text-mizizi-gold">Serves</div><div className="font-serif text-2xl">{recipe.servings}</div></li>
          </ul>
          <div className="mt-8 text-center">
            <Link to="/shop" data-testid="detail-unlock-cta" className="pill pill-primary">Unlock the Full Recipe</Link>
          </div>
        </div>
      )}
    </div>
  );
}
